import { useState } from "react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SERVICES } from "@/data/content";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const TINTS = {
  mauve: "bg-mauve-light text-mauve-dark",
  navy: "bg-greige text-navy",
  gold: "bg-gold-light text-gold-dark",
  sage: "bg-sage-light text-sage-dark",
};

const BADGE_TINTS = {
  Popular: "bg-mauve-light text-mauve-dark",
  Wellbeing: "bg-sage-light text-sage-dark",
  Enrichment: "bg-gold-light text-gold-dark",
  Reassurance: "bg-greige text-navy",
  "Modern Care": "bg-mauve-light text-mauve-dark",
  Vitality: "bg-sage-light text-sage-dark",
};

export const Services = ({ onBook }) => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="bg-cream-soft py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>What We Offer</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl">
            Tailored non-medical companionship services
          </h2>
          <p className="mt-5 text-base leading-relaxed text-charcoal/75">
            No sterile routines. Every engagement is handcrafted around your loved one's
            lifestyle, energy, and cherished habits.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <Reveal key={service.id} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`service-card-${service.id}`}
                  className="group flex h-full flex-col rounded-3xl border border-navy/[0.06] bg-white p-8 shadow-ambient transition-all duration-300 hover:-translate-y-1.5 hover:border-mauve/40 hover:shadow-elevated"
                >
                  <div className="flex items-start justify-between">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl ${TINTS[service.tint]}`}
                    >
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.18em] ${BADGE_TINTS[service.badge]}`}
                    >
                      {service.badge}
                    </span>
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-navy">{service.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal/70">
                    {service.summary}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelected(service)}
                    data-testid={`service-discover-${service.id}`}
                    className="mt-6 inline-flex items-center gap-2 self-start text-sm font-semibold text-mauve-dark transition-colors hover:text-navy"
                  >
                    Discover
                    <ArrowUpRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      aria-hidden="true"
                    />
                  </button>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent
          data-testid="service-detail-dialog"
          className="max-w-lg rounded-3xl border-navy/10 bg-cream"
        >
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-navy">
                  {selected.title}
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed text-charcoal/70">
                  {selected.summary}
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-2 space-y-3">
                {selected.details.map((detail) => (
                  <li key={detail} className="flex items-start gap-3 text-sm text-charcoal/80">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage-dark">
                      <Check className="h-3 w-3" aria-hidden="true" />
                    </span>
                    {detail}
                  </li>
                ))}
              </ul>
              <p className="mt-5 rounded-2xl bg-gold-light p-4 text-xs leading-relaxed text-charcoal/70">
                Every visit is strictly non-medical — no personal care, no medication
                handling. Just unhurried, genuine company.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  onBook();
                }}
                data-testid="service-detail-book-button"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-mauve-dark"
              >
                Book a Consultation
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
